import { isEmpty } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { v4 } from 'uuid';

import AddFixing from '@/App/tiles/ForwardAccumulator/parts/RightPanel/Scheduler/Header/AddFixing.tsx';
import ExportSchedule from '@/App/tiles/ForwardAccumulator/parts/RightPanel/Scheduler/Header/ExportSchedule.tsx';
import ResetScheduler from '@/App/tiles/ForwardAccumulator/parts/RightPanel/Scheduler/Header/ResetScheduler.tsx';
import {
  useCheckForwardAccumulatorByTradeCapture,
  useForwardAccumulatorTileId,
} from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { type ImportedFowardScheduleRow, mapForwardScheduleToFixing } from '@/App/tiles/shared/import/accuSchedule.ts';
import ImportSchedule from '@/App/tiles/TargetAccumulator/Schedule/ScheduleHeader/ImportSchedule.tsx';
import { isDefined } from '@/components/sg-design-system/utils/index.ts';
import { selectForwardAccumulatorCustomFields } from '@/store/forwardAccumulator/selectors.ts';
import type { AccumulatorScheduleLine } from '@/store/forwardAccumulator/state.ts';
import { useAppSelector } from '@/store/hooks.ts';

type Props = {
  parametersAreVisible?: boolean;
  setParametersAreVisible?: (isVisible: boolean) => void;
};

const Header = ({ parametersAreVisible = true, setParametersAreVisible }: Props) => {
  const tileId = useForwardAccumulatorTileId();
  const check = useCheckForwardAccumulatorByTradeCapture();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const customFields = useAppSelector((state) => selectForwardAccumulatorCustomFields(state, tileId));
  const scheduleIds = useAppSelector((state) => state.forwardAccumulator[tileId]?.properties.scheduleIds);

  const isModified = isDefined(customFields) && !isEmpty(customFields);
  const shouldShowConfirmation = isDefined(scheduleIds) && !isEmpty(scheduleIds);

  const toggleTableVisibility = useCallback(() => {
    sgwtWebAnalytics?.trackEvent(
      'Forward Accumulator',
      parametersAreVisible ? 'Parameters hidden' : 'Parameters shown',
    );
    setParametersAreVisible?.(!parametersAreVisible);
  }, [parametersAreVisible, setParametersAreVisible, sgwtWebAnalytics]);

  const resetSchedule = () => {
    sgwtWebAnalytics?.trackEvent('Forward Accumulator', 'Schedule reset');
    return check({
      schedule: null,
    });
  };

  const addLineSchedule = () => {
    sgwtWebAnalytics?.trackEvent('Forward Accumulator', 'Schedule line added');
    const newId = v4();
    return check({
      schedule: { [newId]: {} as AccumulatorScheduleLine },
    });
  };

  const onImportSchedule = (rows: readonly ImportedFowardScheduleRow[]) => {
    sgwtWebAnalytics?.trackEvent('Forward Accumulator', 'Schedule imported');
    const legsPatch = Object.fromEntries(rows.map((row) => [v4(), mapForwardScheduleToFixing(row)]));

    const oldSchedule =
      scheduleIds?.reduce(
        (acc, id) => {
          acc[id] = null;
          return acc;
        },
        {} as Record<string, null>,
      ) ?? {};

    return check({
      schedule: { ...oldSchedule, ...legsPatch },
    });
  };

  return (
    <ul className="nav w-100">
      <li className="nav-item cursor-pointer" onClick={toggleTableVisibility}>
        <span className="nav-link active ms-16px">
          <FormattedMessage id="accumulator.scheduler.header" />
        </span>
      </li>

      <li className="nav-item form-inline ms-auto">
        <div className="nav-link pe-2">
          <ResetScheduler isScheduleModified={isModified} resetSchedule={resetSchedule} />

          <ExportSchedule />

          <ImportSchedule
            accuType="Standard"
            onImportSchedule={onImportSchedule}
            shouldShowConfirmation={shouldShowConfirmation}
          />

          <AddFixing addLineSchedule={addLineSchedule} />

          {isDefined(setParametersAreVisible) && (
            <button type="button" className="btn btn-sm btn-link btn-icon-text pe-0" onClick={toggleTableVisibility}>
              <i className="icon icon-sm line-height-1 btn-group text-xxlarge ms-auto">
                {parametersAreVisible ? 'unfold_more' : 'unfold_less'}
              </i>
            </button>
          )}
        </div>
      </li>
    </ul>
  );
};

export default Header;
